'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { DefaultValues, FieldValues, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import z from 'zod'

import { RECAPTCHA_ACTIONS } from '@/constants'
import { createSubscriber } from '@/lib/actions/subscriber.action'
import { getRecaptchaToken } from '@/lib/recaptcha'
import { NewsletterSubscriptionSchema } from '@/lib/validations'

import { Button } from '../ui/button'

const NewsletterForm = () => {
    const defaultValues = {
        firstname: '',
        lastname: '',
        email: '',
        agreedToTerms: false,
    }

    const {
        register,
        reset,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<z.infer<typeof NewsletterSubscriptionSchema>>({
        resolver: zodResolver(NewsletterSubscriptionSchema),
        defaultValues: defaultValues as DefaultValues<FieldValues>,
    })

    const onSubmit = async (
        data: z.infer<typeof NewsletterSubscriptionSchema>
    ) => {
        const recaptchaToken = await getRecaptchaToken(
            RECAPTCHA_ACTIONS.NEWSLETTER_FORM
        )

        const result = await createSubscriber(recaptchaToken, data)

        if (result?.success) {
            if (result?.data?.verified) {
                toast.success('Subscription Successful', {
                    description: 'Thank you for subscribing to our newsletter!',
                })
            } else {
                toast.success('Subscription Pending', {
                    description:
                        'Please check your email to confirm your subscription.',
                })
            }

            reset()
        } else {
            toast.error('Error', {
                description: result?.error?.message,
            })
        }
    }

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className="font-montserrat flex flex-col gap-4 md:max-w-[500px] mx-auto md:mr-0"
        >
            <div className="flex flex-col md:flex-row justify-end gap-4">
                <div className="w-full flex-1 flex-col sm:inline-flex sm:w-auto text-left">
                    <label htmlFor="email" className="sr-only">
                        Email
                    </label>
                    <input
                        type="email"
                        className="bg-transparent text-white flex-1 w-full form-field py-5 px-4 text-xs border border-[#52b3d8] placeholder:uppercase"
                        placeholder="email"
                        id="email"
                        {...register('email')}
                    />
                    {errors.email && (
                        <p className="text-red-300 text-sm mt-1">
                            {errors.email.message}
                        </p>
                    )}
                </div>
                <div className="w-full flex-col sm:inline-flex sm:w-auto">
                    <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full sm:w-auto h-auto rounded-none py-5 px-9 text-xs uppercase bg-[#52b3d899] border border-[#52b3d8] hover:bg-[#52b3d8] text-white hover:text-[#232f5f] hover:cursor-pointer transition"
                    >
                        Join
                    </Button>
                </div>
            </div>
        </form>
    )
}

export default NewsletterForm
